import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, Button, Menu, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { PopoverOrigin } from '@mui/material/Popover';
import InboxIcon from '@mui/icons-material/MoveToInbox';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import HideSourceIcon from '@mui/icons-material/HideSourceSharp';
import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { DateTime, Duration } from 'luxon';
import { useFormContext } from 'react-hook-form';
import {
  ProgressButton,
  SimpleDialog,
  BackgroundTimer
} from 'modules/common/components';
import { useDialogState, DialogCancelProps } from 'modules/common/hooks';
import { TimeSignal, TimeSignalClassification } from 'graphql/types';
import { useTrackRenderCounts } from 'modules/common/components/Render';
import { useFormErrorDialogContext } from 'modules/notes/common';
import { useFeatureFlag } from 'modules/common/hooks';
import { useIsSmartSuggestionSettingEnabled } from 'modules/timesheet-labels/hooks';
import { useNotesBackgroudTimer } from 'modules/NotesBackgroudTimer/hooks/useNotesBackgroudTimer';
import { useNotesBackgroudTimerContext } from 'modules/NotesBackgroudTimer/context/NotesBackgroudTimerContext';
import { mapFormFieldValuesToNoteData } from 'modules/notes/common';
import { notesBackgroundStorage } from 'modules/NotesBackgroudTimer/utils/NotesBackgroundStorage';
import { useTimerContext } from '../Timer';
import { NoteData } from '../../noteSchema';
import { BulkSuggestionAcceptAllActions } from '../../../../timesheet-labels/BulkSuggestionAcceptAllActions';
import NoteActionMenutItem from './NoteActionMenutItem';

export const menuProps: {
  anchorOrigin?: PopoverOrigin | undefined;
  transformOrigin?: PopoverOrigin | undefined;
} = {
  anchorOrigin: {
    vertical: 'top',
    horizontal: 'center'
  },
  transformOrigin: {
    vertical: 'bottom',
    horizontal: 'center'
  }
};

export const useMenuStyles = makeStyles((theme) => ({
  paper: {
    boxShadow: theme.shadow?.paper
  }
}));

export const menuItems = [
  {
    icon: <ThumbUpIcon fontSize="small" />,
    title: 'timeSignalActions.accepted',
    classification: TimeSignalClassification.Accepted
  },
  {
    icon: <HideSourceIcon fontSize="small" />,
    title: 'timeSignalActions.private',
    classification: TimeSignalClassification.Private
  },
  {
    icon: <InboxIcon fontSize="small" />,
    title: 'timeSignalActions.inbox',
    classification: TimeSignalClassification.Unclassified
  }
];

export const messages = {
  addStopTimerDialog: [
    <FormattedMessage
      key="note.stopTimerDialog.addMessage"
      id="note.stopTimerDialog.addMessage"
    />,
    <FormattedMessage
      key="note.stopTimerDialog.message2"
      id="note.stopTimerDialog.message2"
    />
  ],
  updateStopTimerDialog: [
    <FormattedMessage
      key="note.stopTimerDialog.updateMessage"
      id="note.stopTimerDialog.updateMessage"
    />,
    <FormattedMessage
      key="note.stopTimerDialog.message2"
      id="note.stopTimerDialog.message2"
    />
  ],
  cancelDialog: [
    <FormattedMessage
      key="note.cancelDialog.message1"
      id="note.cancelDialog.message1"
    />,
    <FormattedMessage
      key="note.cancelDialog.message2"
      id="note.cancelDialog.message2"
    />
  ],
  stopTimerOk: <FormattedMessage id="note.stopTimerDialog.yes" />,
  stopTimerCancel: <FormattedMessage id="note.stopTimerDialog.cancel" />,
  cancelDialogYes: <FormattedMessage id="note.cancelDialog.yes" />,
  cancelDialogNo: <FormattedMessage id="note.cancelDialog.no" />
};
const useStyles = makeStyles((theme) => ({
  addNote: {
    fontWeight: 600
  },
  cancel: {
    marginRight: theme.spacing(1)
  },
  container: {
    width: '100%',
    display: 'flex',
    justifyContent: ({ isSuggestion }: { isSuggestion: boolean }) =>
      isSuggestion ? 'space-between' : 'flex-end',
    padding: theme.spacing(2, 0)
  },
  action: {
    display: 'flex',
    justifyContent: ({ isSuggestion }: { isSuggestion: boolean }) =>
      isSuggestion ? 'space-between' : 'flex-end'
  },
  accept: {
    display: 'flex'
  }
}));

type NoteFooterProps = DialogCancelProps & {
  handleSubmit: UseFormReturn['handleSubmit'];
  onSubmitNote: (note: NoteData) => Promise<void>;
  loading: boolean;
  isValidForm: boolean;
  saveTitle: string;
  onClassificationChange(classification: TimeSignalClassification | null): void;
  mode?: 'add' | 'edit';
  classification?: TimeSignalClassification | null;
  onSaveNoteWithTimerRunning(
    duration: Duration | null,
    showStartEndTime?: boolean
  ): void;
  showStartEndTime: boolean;
  customLabels: string;
  timesheetLabels: string;
  overrideClass?: string;
};

const useSaveAction = ({
  handleSubmit,
  onSubmitNote,
  handleDuration,
  timeSignal,
  isValidForm,
  isTiwInlineTimerEnabled
}: {
  handleSubmit: UseFormReturn['handleSubmit'];
  onSubmitNote: (note: NoteData) => Promise<void>;
  handleDuration(duration: Duration | null): void;
  isValidForm: boolean;
  timeSignal?: TimeSignal;
  isTiwInlineTimerEnabled?: boolean;
}) => {
  const { running, stopTimer, startTime, setStartTime } = useTimerContext();
  const { onStart } = useNotesBackgroudTimer();
  const {
    setDialogTimerRunning,
    running: isBackgroundTimerRunning,
    resetSignalContextValues,
    signalMeta,
    updateBackgroundTimerMeta,
    setSaveStartTime
  } = useNotesBackgroudTimerContext();
  const { remove } = notesBackgroundStorage;
  const [disabled, setDisabled] = useState(false);

  useMemo(() => {
    if (disabled) {
      setDisabled(isValidForm);
    }
  }, [disabled, isValidForm]);

  const {
    open: openSave,
    openDialog: openSaveDialog,
    closeDialog: closeSaveDialog
  } = useDialogState();

  const saveNote = useCallback(async () => {
    if (startTime) {
      handleDuration(DateTime.local().diff(startTime));
    }
    stopTimer();
    setStartTime(null);
    setDisabled(true);
    closeSaveDialog();
    await handleSubmit(onSubmitNote)();
    setDialogTimerRunning(false);

    if (isTiwInlineTimerEnabled && isBackgroundTimerRunning) {
      resetSignalContextValues();
      remove();
    }
  }, [
    closeSaveDialog,
    handleDuration,
    handleSubmit,
    onSubmitNote,
    setStartTime,
    startTime,
    stopTimer,
    isTiwInlineTimerEnabled,
    setDialogTimerRunning,
    isBackgroundTimerRunning,
    resetSignalContextValues,
    remove
  ]);

  const keepTimerRunning = useCallback(async () => {
    setDisabled(true);
    closeSaveDialog();
    await handleSubmit(onSubmitNote)();
    setDialogTimerRunning(false);
    setSaveStartTime(false);
    if (timeSignal?.id && !isBackgroundTimerRunning) {
      onStart({ note: timeSignal });
    } else if (
      isBackgroundTimerRunning &&
      timeSignal?.id &&
      timeSignal?.description !== signalMeta?.description
    ) {
      updateBackgroundTimerMeta(timeSignal?.description ?? undefined);
    }
  }, [
    closeSaveDialog,
    handleSubmit,
    onSubmitNote,
    onStart,
    isBackgroundTimerRunning,
    timeSignal,
    signalMeta,
    setDialogTimerRunning,
    updateBackgroundTimerMeta,
    setSaveStartTime
  ]);

  const onSaveClick = useCallback(async () => {
    if (running) {
      openSaveDialog();
      return;
    }
    setDisabled(true);
    setDialogTimerRunning(false);
    await handleSubmit(onSubmitNote)();
  }, [
    running,
    handleSubmit,
    onSubmitNote,
    openSaveDialog,
    setDialogTimerRunning
  ]);

  return useMemo(
    () => ({
      openSave,
      onSaveClick,
      saveNote,
      closeSaveDialog,
      disabled,
      keepTimerRunning
    }),
    [
      closeSaveDialog,
      disabled,
      onSaveClick,
      openSave,
      saveNote,
      keepTimerRunning
    ]
  );
};

const NoteFooterFC: React.FC<NoteFooterProps> = ({
  handleSubmit,
  onSubmitNote,
  loading,
  isValidForm,
  onClassificationChange,
  openCancel,
  onNoteDiscard,
  discardNote,
  closeCancelDialog,
  mode = 'add',
  classification,
  onSaveNoteWithTimerRunning: handleDuration,
  customLabels,
  timesheetLabels
}: NoteFooterProps) => {
  useTrackRenderCounts('note-dialog-footer');
  const isSmartSuggestionEnabled = useIsSmartSuggestionSettingEnabled();
  const classes = useStyles({ isSuggestion: isSmartSuggestionEnabled });
  const isTiwInlineTimerEnabled: boolean = useFeatureFlag(
    'isTiwInlineTimerEnabled'
  );
  const { getValues } = useFormContext();
  const formValues = getValues();
  const timeSignal = isTiwInlineTimerEnabled
    ? mapFormFieldValuesToNoteData(formValues)
    : undefined;
  const { errorContext } = useFormErrorDialogContext();
  const {
    openSave,
    onSaveClick,
    closeSaveDialog,
    saveNote,
    disabled,
    keepTimerRunning
  } = useSaveAction({
    handleSubmit,
    onSubmitNote,
    handleDuration,
    isValidForm,
    timeSignal,
    isTiwInlineTimerEnabled
  });

  const [anchorEl, setAnchorEl] = useState<any>(null);
  const [selectedClassification, setSelectedClassification] = useState<
    TimeSignalClassification | undefined
  >(undefined);

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    },
    [anchorEl]
  );
  const onCloseMenu = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const onSave = useCallback(
    (classification: TimeSignalClassification) => {
      onClassificationChange(classification);
      onCloseMenu();
      onSaveClick();
      setSelectedClassification(classification);
    },
    [
      onClassificationChange,
      onSaveClick,
      onCloseMenu,
      setSelectedClassification
    ]
  );
  const menuClasses = useMenuStyles();
  const nextFocus = useRef<HTMLButtonElement>(null);

  return (
    <>
      <Box className={classes.container}>
        {isSmartSuggestionEnabled && (
          <div>
            <BulkSuggestionAcceptAllActions
              nextFocus={nextFocus}
              customLabels={customLabels}
              timesheetLabels={timesheetLabels}
            />
          </div>
        )}
        <div className={classes.action}>
          <Button
            ref={nextFocus}
            data-testid="add-note-cancel-btn"
            variant="text"
            className={classes.cancel}
            onClick={onNoteDiscard}
          >
            <Typography color={'textSecondary'}>
              <FormattedMessage id="note.cancel" />
            </Typography>
          </Button>
          <ProgressButton
            data-testid="add-note-submit-btn"
            variant="contained"
            color="primary"
            disabled={
              !isValidForm ||
              disabled ||
              errorContext.noteExceeded ||
              errorContext.commentExceeded
            }
            loading={loading}
            className={classes.addNote}
            onClick={handleClick}
          >
            <FormattedMessage id="note.saveNote" />
          </ProgressButton>
          <Menu
            elevation={0}
            data-testid="note-actions-menu"
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={onCloseMenu}
            classes={menuClasses}
            {...menuProps}
          >
            {menuItems.map(
              ({ title, icon, classification: itemClassification }) => (
                <NoteActionMenutItem
                  key={title}
                  title={title}
                  icon={icon}
                  classification={itemClassification}
                  selectedClassification={classification}
                  onSave={onSave}
                  disabled={!isValidForm || disabled}
                />
              )
            )}
          </Menu>
        </div>
      </Box>
      {openSave &&
        (isTiwInlineTimerEnabled ? (
          <BackgroundTimer
            open={openSave}
            onClose={closeSaveDialog}
            onSecondaryAction={closeSaveDialog}
            onPrimaryAction={saveNote}
            selectedClassification={selectedClassification}
            keepTimerRunningAction={keepTimerRunning}
          />
        ) : (
          <SimpleDialog
            open={openSave}
            onClose={closeSaveDialog}
            onPrimaryAction={closeSaveDialog}
            onSecondaryAction={saveNote}
            title={<FormattedMessage id="note.stopTimerDialog.title" />}
            messages={
              mode === 'add'
                ? messages.addStopTimerDialog
                : messages.updateStopTimerDialog
            }
            primaryActionButtonTitle={messages.stopTimerCancel}
            secondaryActionButtonTitle={messages.stopTimerOk}
          />
        ))}
      {openCancel && (
        <SimpleDialog
          open={openCancel}
          onClose={closeCancelDialog}
          onPrimaryAction={closeCancelDialog}
          onSecondaryAction={discardNote}
          title={<FormattedMessage id="note.cancelDialog.title" />}
          messages={messages.cancelDialog}
          primaryActionButtonTitle={messages.cancelDialogNo}
          secondaryActionButtonTitle={messages.cancelDialogYes}
        />
      )}
    </>
  );
};

export const NoteFooter = React.memo(NoteFooterFC);
